import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient, QueryKey } from '@tanstack/react-query';

import { CTL_ENDPOINTS } from '../../api/endpoints';
import { ctlUrlOf } from '../../api/http';
import { deliverStreamReady, deliverStreamSignal, onStreamReady, onStreamSignal } from '../../stream/signals';
import { ctlKeys } from '../../utils/query-keys';
import { mySettingsQueryOptions } from '../../utils/queryOptions';
import { rereadNow } from '../../utils/rereadNow';
import { issueKey, issuesKey } from '../logic/issueQuery';

import { useSignalStream } from './useSignalStream';

const settingsKeys = (): readonly QueryKey[] => [ctlKeys.settings.me(), ctlKeys.policyAll(), ctlKeys.routingAll()];

const catchUpKeys = (): readonly QueryKey[] => [ctlKeys.feed.me(), ...settingsKeys(), issuesKey()];

const listenForCtlSignals = (queryClient: QueryClient): (() => void) => {
  const reread = (queryKey: QueryKey): void => {
    void rereadNow(queryClient, queryKey);
  };
  const unsubscribes = [
    onStreamReady(() => catchUpKeys().forEach(reread)),
    onStreamSignal('feed', () => reread(ctlKeys.feed.me())),
    onStreamSignal('settings', () => settingsKeys().forEach(reread)),
    onStreamSignal('issue', ({ issueId }) => {
      reread(ctlKeys.feed.me());
      reread(issueKey(issueId));
    }),
  ];
  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
};

const renewSession = (queryClient: QueryClient): Promise<unknown> =>
  queryClient.fetchQuery({ ...mySettingsQueryOptions(), staleTime: 0 }).catch(() => undefined);

export const useNotificationStream = (): void => {
  const queryClient = useQueryClient();
  useEffect(() => listenForCtlSignals(queryClient), [queryClient]);
  useSignalStream(ctlUrlOf(CTL_ENDPOINTS.STREAM), {
    onReady: deliverStreamReady,
    onSignal: deliverStreamSignal,
    renew: () => renewSession(queryClient),
  });
};
